import { useCallback, useEffect, useState } from 'react'

import { Drawer, Layout } from '@loadsmart/miranda-react'

import { type PreferredTrackingMethod } from '../../../domain/PreferredTrackingMethod'
import { type TrackingMethod, type TrackingProvider } from '../../../domain/Tracking'
import FinalizeConnectionStep from './FinalizeConnectionStep'
import PreferredProviderStep from './PreferredProviderStep'
import TrackingMethodStep from './TrackingMethodStep'
import { useTrackingOnboardingContext } from './TrackingOnboardingContext'

export type SetupTrackingConnectionDrawerProps = {
  readonly open: boolean
  readonly onClose: () => void
}

export default function SetupTrackingConnectionDrawer({
  open,
  onClose,
}: SetupTrackingConnectionDrawerProps) {
  const { preferredTrackingMethod } = useTrackingOnboardingContext()

  const [preferred, setPreferred] = useState<PreferredTrackingMethod | undefined>()

  useEffect(() => {
    setPreferred(preferredTrackingMethod)
  }, [preferredTrackingMethod])

  const { method, provider } = preferred ?? {}

  const handleOnSelectMethod = useCallback((method: TrackingMethod) => {
    setPreferred({ method, provider: undefined })
  }, [])

  const handleOnSelectProvider = useCallback((provider: TrackingProvider) => {
    setPreferred(previousState => ({ ...previousState, provider }))
  }, [])

  const handleOnClose = useCallback(() => {
    onClose()
    setPreferred(preferredTrackingMethod)
  }, [onClose, preferredTrackingMethod])

  return (
    <Drawer open={open} divided size="medium" data-testid="onboarding-tracking-drawer">
      <Drawer.Header>
        Setup tracking connection
        <Drawer.Close
          onClose={handleOnClose}
          aria-label="tracking onboarding drawer close button"
        />
      </Drawer.Header>
      <Drawer.Body style={{ height: '100%' }}>
        <Layout.Stack>
          <TrackingMethodStep selectedMethod={method} onSelectMethod={handleOnSelectMethod} />
          <PreferredProviderStep
            selectedMethod={method}
            selectedProvider={provider}
            onSelectProvider={handleOnSelectProvider}
          />
          <FinalizeConnectionStep
            selectedMethod={method}
            selectedProvider={provider}
            onConfirmed={handleOnClose}
          />
        </Layout.Stack>
      </Drawer.Body>
    </Drawer>
  )
}
